// @ts-strict-ignore
import Button from 'antd/lib/button';
import Skeleton from 'antd/lib/skeleton';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import * as React from 'react';
import {
    AppPlatform,
    AppStoreType,
    AutomationType,
    FeatureFlag,
    FeatureFlagTimeSeriesDatum,
    MonitoringMetricHealthStatus,
    StabilityMetricType,
    StatisticUnit,
    SubscriptionFeatureType,
    UserRatingsMetricType,
} from '@/types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { ChartColors, useChartColors, useChartTheme } from '../styles/themeMode';
import { ArrowRightOutlined, FlagFilled, SwapOutlined, WarningFilled } from '@ant-design/icons';

import { AdoptionXAxisSeriesPoint, GroupedMonitoringStatistic, MonitoringStatisticGroup } from '@/typescriptHelpers';
import { Area, AreaConfig } from '@ant-design/plots';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '@/timeHelpers';
import { forIn, groupBy, isNil, max, min } from 'lodash';
import { Annotation } from '@antv/g2plot';
import autoHaltIcon from '../../../assets/autoHaltIcon.png';
import autoAccelerateIcon from '../../../assets/autoAccelerateIcon.png';
import { DescriptionPopoverIcon } from '../common/DescriptionPopoverIcon';
import { generateAutomationThunderboltIcon, renamedMetricTooltip } from './monitoringHelpers';
import {
    displayThresholdForMonitoringStatisticGroup,
    displayFormattedMonitoringMetricValue,
    phasedReleasePausedDisplayTextForAppStoreType,
} from '@/helpers/displayHelpers';
import {
    currentReleaseSeriesLabelForMetricType,
    decimalPlacesForStatValue,
    isPercentageMonitoringMetric,
    limitsPerUnit,
    metricTypeSupportsBenchmark,
    specificUnitForMetricType,
    statTypeForMonitoringMetricType,
    unitForMonitoringMetricType,
} from '@/helpers';
import { createRoot } from 'react-dom/client';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import Popover from 'antd/lib/popover';
import { FeatureFlagChangeList } from '../featureFlags/FeatureFlagChanges';
import IntegrationIcon from '../icons/IntegrationIcon';

const formatPastDate = (timestamp: string) => {
    return dayjs(timestamp).local().calendar(null, {
        sameDay: '[Today]',
        lastDay: DatetimeFormat.ShortMonthDate,
        lastWeek: DatetimeFormat.ShortMonthDate,
        nextDay: DatetimeFormat.ShortMonthDate,
        nextWeek: DatetimeFormat.ShortMonthDate,
        sameElse: DatetimeFormat.ShortMonthDate,
    });
};

const formatRecentDate = (timestamp: string) => {
    return dayjs.utc(timestamp).local().format(DatetimeFormat.Hour);
};

const dropTimezone = (timestamp: string) => {
    return dayjs.utc(timestamp).format('YYYY-MM-DDTHH:mm:ss');
};

const generateGraphConfig: (
    args: MonitoringStatisticGroup,
    featureFlags: FeatureFlagTimeSeriesDatum[],
    releasedAt: string,
    appStoreType: AppStoreType,
    chartColors: ChartColors,
    shouldShowDisabledUI?: boolean,
    onShowFeatureFlag?: (flag: FeatureFlag) => void,
    onPopoverOpen?: () => void,
    adoptionXAxisSeries?: AdoptionXAxisSeriesPoint[],
) => AreaConfig = (
    {
        dataPoints,
        threshold,
        isMoreBetter,
        type,
        integrationType,
        eventName,
        status,
        hasRecentDataOnly,
        automationUsage,
        currentValue,
        additionalDataSeries,
    },
    featureFlags,
    releasedAt,
    appStoreType,
    chartColors,
    shouldShowDisabledUI,
    onShowFeatureFlag,
    onPopoverOpen,
    adoptionXAxisSeries,
) => {
    const forcedUnit =
        isPercentageMonitoringMetric(type, integrationType, eventName) ? StatisticUnit.Percent : undefined;

    const aboveThresholdColor = isMoreBetter ? runwayColors.statusGreen : runwayColors.statusRed;
    const belowThresholdColor = isMoreBetter ? runwayColors.statusRed : runwayColors.statusGreen;

    const releasedAtMoment = dayjs.utc(releasedAt);
    const releasedAtTimestamp =
        hasRecentDataOnly || !releasedAtMoment.isValid() ?
            releasedAtMoment.valueOf()
        :   dayjs(dropTimezone(releasedAtMoment.startOf('day').toISOString())).valueOf();
    const releasedAtUtcMs = releasedAtMoment.valueOf();

    // When an adoption series is provided, the x-axis is switched from time to
    // adoption %. Every x position (data points + annotations) is mapped from its
    // true UTC time to an adoption % by linear interpolation along that series.
    // The adoption-rate card itself is excluded: plotting adoption vs. adoption
    // would just be an identity line, so it always stays time-based.
    const adoptionMode =
        !!(adoptionXAxisSeries && adoptionXAxisSeries.length > 0) && type !== StabilityMetricType.AdoptionRate;
    const interpolateAdoption = (utcMs: number): number => {
        const series = adoptionXAxisSeries!;
        if (utcMs <= series[0].ts) {
            return series[0].pct;
        }
        const lastPoint = series[series.length - 1];
        if (utcMs >= lastPoint.ts) {
            return lastPoint.pct;
        }
        for (let i = 1; i < series.length; i++) {
            if (utcMs <= series[i].ts) {
                const prev = series[i - 1];
                const next = series[i];
                const span = next.ts - prev.ts || 1;
                const fraction = (utcMs - prev.ts) / span;
                return prev.pct + fraction * (next.pct - prev.pct);
            }
        }
        return lastPoint.pct;
    };
    // x position for a point/annotation: the display timestamp in time mode
    // (unchanged behavior), or the interpolated adoption % in adoption mode.
    const xPosition = (utcMs: number, displayMs: number): number =>
        adoptionMode ? interpolateAdoption(utcMs) : displayMs;

    let annotations: Annotation[] = [];

    const triggeredAccelerateAutomationAt = automationUsage[AutomationType.AutoPromoteStablePhasedReleases].triggeredAt;
    const triggeredHaltAutomationAt = automationUsage[AutomationType.AutoHaltUnstablePhasedReleases].triggeredAt;

    const mappedDataPoints =
        dataPoints?.map((data) => {
            // non recent data has day granularity, ignore the time part
            const day = hasRecentDataOnly ? dayjs.utc(data.timestamp) : dayjs(dropTimezone(data.timestamp));
            const timestamp = day.valueOf();
            const trueTs = dayjs.utc(data.timestamp).valueOf();

            const normalizedValue = data.value - (threshold ?? 0);

            const decimalPlaces = decimalPlacesForStatValue(
                unitForMonitoringMetricType(type),
                normalizedValue,
                statTypeForMonitoringMetricType(type),
            );

            const value = Number(normalizedValue.toFixed(decimalPlaces ?? 0));
            return {
                timestamp,
                x: xPosition(trueTs, timestamp),
                value,
            };
        }) ?? [];

    // if there is not enough data, don't show the graph
    if (mappedDataPoints.length < 1 || isNil(currentValue)) {
        return null;
    }

    if (mappedDataPoints.length === 1) {
        // if there is only one data point, add another one to make the graph look like a line
        mappedDataPoints.push({
            timestamp: releasedAtTimestamp - 10000,
            x: xPosition(releasedAtUtcMs - 10000, releasedAtTimestamp - 10000),
            value: mappedDataPoints[0].value,
        });
    }

    mappedDataPoints.sort((a, b) => a.timestamp - b.timestamp);

    const allDataPoints = mappedDataPoints;
    const hasBenchmark =
        additionalDataSeries && Object.keys(additionalDataSeries).length > 0 && metricTypeSupportsBenchmark(type);

    const sampleValues = mappedDataPoints.map((dataPoint) => dataPoint.value);

    const lastDataPoint = mappedDataPoints[mappedDataPoints.length - 1];
    const firstDataPoint = mappedDataPoints[0];

    let color = chartColors.axisLabel;

    if (status === MonitoringMetricHealthStatus.Healthy) {
        color = runwayColors.statusGreen;
    } else if (status === MonitoringMetricHealthStatus.Unhealthy) {
        color = runwayColors.statusRed;
    }

    annotations.push({
        type: 'dataMarker',
        top: true,
        line: null,
        position: [lastDataPoint.x, lastDataPoint.value],
        point: {
            style: {
                stroke: chartColors.tooltipText,
                r: 8,
                lineWidth: 4,
                fill: color,
            },
        },
    });

    // annotations for feature flags

    let ffAnnotations = [];

    if (hasRecentDataOnly) {
        ffAnnotations = featureFlags.map((ff) => {
            const ffUtcMs = dayjs.utc(ff.timestamp).valueOf();
            return {
                position: [xPosition(ffUtcMs, ffUtcMs), 'max'],
                type: 'html',
                offsetY: -36,
                html: () => {
                    const node = document.createElement('div');
                    const root = createRoot(node);
                    root.render(
                        <div>
                            <div
                                style={{
                                    height: 122,
                                    opacity: 1,
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    gap: 5,
                                }}
                            >
                                <FeatureFlagPopover
                                    onShowFeatureFlag={onShowFeatureFlag}
                                    datums={[ff]}
                                    onPopoverOpen={onPopoverOpen}
                                />
                                <div
                                    style={{
                                        width: 2,
                                        borderLeft: `2px dashed ${runwayColors.primary}`,
                                        flexGrow: 1,
                                        opacity: 0.4,
                                    }}
                                />
                            </div>
                        </div>,
                    );
                    return node;
                },
            } as Annotation;
        });
    } else {
        // long-range data has day-level granularity, so we group all feature flag changes within a single day.
        // Group by the viewer's local day so the popover title matches the "Detected" times shown inside it.
        const featureFlagDataPointsGroupedByDay = groupBy(featureFlags, (flag) =>
            dayjs.utc(flag.timestamp).local().startOf('d').valueOf(),
        );

        forIn(featureFlagDataPointsGroupedByDay, (groupedByDayFlagChanges, dayKey) => {
            const batchedDate = dayjs(Number(dayKey)).toISOString();
            const dayKeyMs = Number(dayKey);

            const annotation = {
                position: [xPosition(dayKeyMs, dayKeyMs), 'max'],
                type: 'html',
                offsetY: -36,
                html: () => {
                    const node = document.createElement('div');
                    const root = createRoot(node);
                    root.render(
                        <div>
                            <div
                                style={{
                                    height: 122,
                                    opacity: 1,
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    gap: 5,
                                }}
                            >
                                <FeatureFlagPopover
                                    onShowFeatureFlag={onShowFeatureFlag}
                                    datums={groupedByDayFlagChanges}
                                    batchedDay={batchedDate}
                                    onPopoverOpen={onPopoverOpen}
                                />
                                <div
                                    style={{
                                        width: 2,
                                        borderLeft: `2px dashed ${runwayColors.primary}`,
                                        flexGrow: 1,
                                        opacity: 0.4,
                                    }}
                                />
                            </div>
                        </div>,
                    );
                    return node;
                },
            } as Annotation;

            ffAnnotations.push(annotation);
        });
    }

    annotations = annotations.concat(ffAnnotations);

    // normalize data
    const minValue = min(sampleValues);
    const maxValue = max(sampleValues);

    let axisGap = Math.abs(minValue - maxValue) * 0.15;

    let minYAxis = minValue - axisGap;
    let maxYAxis = maxValue + axisGap;
    if (axisGap === 0) {
        // default case
        axisGap = Math.abs(minValue) * 0.5;
        minYAxis = minValue - axisGap;
        maxYAxis = maxValue + axisGap;

        const anyValue = minValue; // min value is the same as all values in this case
        const [minLimit, maxLimit] = limitsPerUnit(unitForMonitoringMetricType(type));
        if (minLimit !== null && maxLimit !== null) {
            const adjustedMinLimit = minLimit - (threshold ?? 0);
            const adjustedMaxLimit = maxLimit - (threshold ?? 0);
            if (anyValue === adjustedMinLimit) {
                minYAxis = adjustedMinLimit;
            } else if (anyValue === adjustedMaxLimit) {
                maxYAxis = adjustedMaxLimit;
            }
        }
    }

    // extend y-axis bounds to include additional series data, capped to avoid excessive dezoom
    if (hasBenchmark) {
        const mainRange = Math.abs(maxValue - minValue) || Math.abs(minValue) * 0.5;
        const maxExtension = mainRange * 2;
        const benchmarkValues: number[] = [];
        Object.values(additionalDataSeries).forEach((points) => {
            points.forEach((p) => {
                benchmarkValues.push(Number((p.value - (threshold ?? 0)).toFixed(2)));
            });
        });
        const benchMin = min(benchmarkValues);
        const benchMax = max(benchmarkValues);
        minYAxis = Math.max(Math.min(minYAxis, benchMin - axisGap), minValue - maxExtension);
        maxYAxis = Math.min(Math.max(maxYAxis, benchMax + axisGap), maxValue + maxExtension);
    }

    const ONE_HOUR = 60 * 60 * 1000;
    const firstDataPointTimestamp = firstDataPoint.timestamp;
    let minXAxis: number;
    let maxXAxis: number;
    if (adoptionMode) {
        const xValues = mappedDataPoints.map((dataPoint) => dataPoint.x);
        const releasedAtX = interpolateAdoption(releasedAtUtcMs);
        minXAxis = Math.min(...xValues, releasedAtX);
        maxXAxis = Math.max(...xValues);
    } else {
        minXAxis = Math.min(releasedAtTimestamp, firstDataPointTimestamp);
        maxXAxis = lastDataPoint.timestamp;
    }

    // Adoption tick labels need enough decimals to stay distinct when the axis
    // is zoomed into a small range (e.g. an early rollout at ~0-1%), otherwise
    // every tick rounds to the same "0%"/"1%". Scale precision to the span.
    const adoptionAxisSpan = maxXAxis - minXAxis;
    const adoptionDecimalPlaces =
        adoptionAxisSpan > 0 ? Math.min(4, Math.max(0, Math.ceil(-Math.log10(adoptionAxisSpan)) + 1)) : 2;

    // add releasedAtTimestamp as a reference line
    const releasedAtHoverAreaWidth = 20;

    annotations.push({
        type: 'html',
        top: true,
        alignX: 'left',
        offsetY: -36,
        offsetX: -releasedAtHoverAreaWidth / 2,
        html: () => {
            const node = document.createElement('div');
            const root = createRoot(node);
            root.render(
                <Tooltip
                    title={
                        <>
                            Released on:
                            <br />
                            {dayjs.utc(releasedAt).local().format(DatetimeFormat.ShortMonthDateTime2)}
                        </>
                    }
                >
                    <div
                        style={{
                            height: 122,
                            width: releasedAtHoverAreaWidth,
                            opacity: 1,
                            display: 'flex',
                            alignItems: 'stretch',
                            justifyContent: 'center',
                        }}
                    >
                        <div
                            style={{
                                width: 2,
                                borderLeft: `2px dashed ${antGrey.grey6}`,
                            }}
                        />
                    </div>
                </Tooltip>,
            );
            return node;
        },
        position: [xPosition(releasedAtUtcMs, releasedAtTimestamp), 'max'],
    });

    // add threshold annotation if it exists
    const hasThreshold = !isNil(threshold);
    if (hasThreshold) {
        const constrainedYValue = Math.min(Math.max(0, minYAxis), maxYAxis);

        annotations.push({
            type: 'line',
            start: [minXAxis - minXAxis * 0.01, constrainedYValue],
            offsetX: -5,
            end: ['end', constrainedYValue],
            style: {
                stroke: chartColors.axisLabel,
                lineDash: [4, 4],
                lineWidth: 2,
            },
        });

        // above threshold area
        if (maxValue >= 0) {
            annotations.push({
                top: false,
                type: 'regionFilter',
                start: ['start', constrainedYValue],
                end: ['end', 'end'],
                color: aboveThresholdColor,
            });
        }

        // below threshold area
        if (minValue <= 0) {
            annotations.push({
                top: false,
                type: 'regionFilter',
                start: ['start', 'start'],
                end: ['end', constrainedYValue],
                color: belowThresholdColor,
            });
        }

        // If the metric triggered an automation, add an annotation for that, it should be rendered
        // on the moment the automation was triggered
        function generateImageAnnotation(
            triggeredAtString: string,
            isAutoHalt: boolean,
            yPosition: number,
            appStoreType: AppStoreType,
        ) {
            const imageSize = 24;
            const triggeredAtDate = dayjs.utc(triggeredAtString);
            const triggeredAt = triggeredAtDate.valueOf();

            const image = isAutoHalt ? autoHaltIcon : autoAccelerateIcon;

            let text = `Runway accelerated this release to 100% of users on:`;
            if (isAutoHalt) {
                text = `Runway ${phasedReleasePausedDisplayTextForAppStoreType(appStoreType).toLowerCase()} this release on:`;
            }

            const triggeredAtFormatted = triggeredAtDate.local().format(DatetimeFormat.ShortMonthDateTime);

            return {
                type: 'html',
                top: true,
                alignX: 'left',
                alignY: 'middle',
                offsetX: -12,
                offsetY: -12,
                html: () => {
                    const node = document.createElement('div');
                    const root = createRoot(node);
                    root.render(
                        <Tooltip
                            showArrow={false}
                            title={
                                <Space direction="vertical" size={0}>
                                    <Typography.Text
                                        style={{
                                            color: antGrey.grey6,
                                        }}
                                    >
                                        {text}
                                        <br />
                                        <span
                                            style={{
                                                color: 'white',
                                            }}
                                        >
                                            {triggeredAtFormatted}
                                        </span>
                                    </Typography.Text>
                                </Space>
                            }
                        >
                            <img
                                src={image}
                                style={{
                                    width: imageSize,
                                    height: imageSize,
                                }}
                            />
                        </Tooltip>,
                    );
                    return node;
                },
                position: [xPosition(triggeredAt, triggeredAt), yPosition],
            } as Annotation;
        }

        annotations.push({
            type: 'html',
            top: true,
            alignX: 'left',
            alignY: 'middle',
            offsetX: 17,
            offsetY: -12,
            html: () => {
                const node = document.createElement('div');
                const root = createRoot(node);
                root.render(
                    <Tooltip
                        showArrow={false}
                        title={
                            <Space direction="vertical" size={0}>
                                <Typography.Text
                                    style={{
                                        color: antGrey.grey6,
                                    }}
                                >
                                    Threshold:
                                </Typography.Text>
                                <Typography.Text
                                    style={{
                                        color: 'white',
                                    }}
                                >
                                    {displayFormattedMonitoringMetricValue(type, threshold, forcedUnit)}
                                </Typography.Text>
                            </Space>
                        }
                    >
                        <WarningFilled style={{ color: antGrey.grey7, fontSize: 22 }} />
                    </Tooltip>,
                );
                return node;
            },
            position: ['max', constrainedYValue],
        });

        const calculateIntersectionPoint = (triggeredAt: string) => {
            const triggeredAtTimestamp = dayjs.utc(triggeredAt).valueOf();

            let triggeredAtDataPoint = null;
            let previousDataPoint = null;

            for (const dataPoint of mappedDataPoints) {
                if (dataPoint.timestamp >= triggeredAtTimestamp) {
                    triggeredAtDataPoint = dataPoint;
                    break;
                }
                previousDataPoint = dataPoint;
            }

            if (triggeredAtDataPoint) {
                let finalPointValue = triggeredAtDataPoint.value;

                if (previousDataPoint) {
                    const slope =
                        (triggeredAtDataPoint.value - previousDataPoint.value) /
                        (triggeredAtDataPoint.timestamp - previousDataPoint.timestamp);

                    finalPointValue =
                        slope * (triggeredAtTimestamp - triggeredAtDataPoint.timestamp) + triggeredAtDataPoint.value;
                }

                // midpoint between the two data points
                return finalPointValue;
            }

            return null;
        };

        if (triggeredAccelerateAutomationAt && !isNil(threshold)) {
            const finalDataPoint = calculateIntersectionPoint(triggeredAccelerateAutomationAt);

            if (finalDataPoint) {
                annotations.push(
                    generateImageAnnotation(triggeredAccelerateAutomationAt, false, finalDataPoint, appStoreType),
                );
            }
        }

        if (triggeredHaltAutomationAt && !isNil(threshold)) {
            const finalDataPoint = calculateIntersectionPoint(triggeredHaltAutomationAt);

            if (finalDataPoint) {
                annotations.push(
                    generateImageAnnotation(triggeredHaltAutomationAt, true, constrainedYValue, appStoreType),
                );
            }
        }
    }

    if (shouldShowDisabledUI) {
        // clear the annotations array if we are to show disabled UI
        annotations.length = 0;
    }

    let finalData = allDataPoints.map((p) => ({ ...p, seriesName: currentReleaseSeriesLabelForMetricType(type) }));

    if (hasBenchmark) {
        Object.entries(additionalDataSeries).forEach(([label, points]) => {
            const mappedPoints = points.map((p) => {
                const normalizedValue = Number((p.value - (threshold ?? 0)).toFixed(2));
                const timestamp = dayjs(p.timestamp).valueOf();
                return {
                    timestamp,
                    x: xPosition(dayjs.utc(p.timestamp).valueOf(), timestamp),
                    value: normalizedValue,
                    seriesName: label,
                };
            });
            finalData = finalData.concat(mappedPoints as any);
        });
    }

    const styleCurrentLine = {
        lineWidth: 4,
        stroke: chartColors.axisLabel,
        lineCap: 'round',
        lineJoin: 'round',
    };

    const styleAdditionalLine = {
        lineWidth: 4,
        stroke: chartColors.secondarySeries,
        lineDash: [4, 4],
        strokeOpacity: 0.7,
    };

    const areaCurrentLineStyle = {
        fillOpacity: hasThreshold ? 0.4 : 0.1,
        fill: hasThreshold ? chartColors.tooltipText : chartColors.axisLabel,
    };

    let lastFormatterTimestamp: number;

    const BASE_GRAPH_CONFIG: AreaConfig = {
        xField: 'x',
        yField: 'value',
        seriesField: hasBenchmark ? 'seriesName' : undefined,
        animation: false,
        isStack: false,
        data: shouldShowDisabledUI ? [] : finalData,
        renderer: 'canvas',
        legend: false,
        meta: {
            x:
                adoptionMode ?
                    {
                        type: 'linear',
                        // Round to the span-appropriate precision, then drop redundant
                        // trailing zeros (5.0% -> 5%, 1.60% -> 1.6%).
                        formatter: (value) =>
                            shouldShowDisabledUI ? '' : `${Number(value.toFixed(adoptionDecimalPlaces))}%`,
                    }
                :   {
                        type: 'time',
                        formatter: (value) => {
                            const formattedDate = hasRecentDataOnly ? formatRecentDate(value) : formatPastDate(value);
                            return shouldShowDisabledUI ? '' : formattedDate;
                        },
                    },
            value: {
                type: 'linear',
                formatter: (value) =>
                    displayFormattedMonitoringMetricValue(
                        type,
                        value + (threshold ?? 0), // compensate for the -threshold above
                        forcedUnit,
                    ),
            },
        },
        color: hasBenchmark ? [chartColors.axisLabel, chartColors.secondarySeries] : chartColors.axisLabel,
        padding: [10, 24, 20, 44],
        yAxis: {
            showLast: true,
            label: null,
            grid: null,
            range: [hasThreshold ? 0.2 : 0, 0.7],
            min: minYAxis,
            max: maxYAxis,
            maxLimit: maxYAxis,
            minLimit: minYAxis,
            nice: true,
        },
        tooltip: {
            fields: ['timestamp', 'value', 'seriesName'],
            showMarkers: true,
            showTitle: hasBenchmark,
            domStyles: {
                'g2-tooltip': {
                    backgroundColor: chartColors.tooltipBg,
                    color: chartColors.tooltipText,
                },
            },
            formatter: (datum) => {
                const { timestamp, value, seriesName } = datum;
                lastFormatterTimestamp = timestamp;
                const momentTimestamp = dayjs.utc(timestamp).local();
                const displayName =
                    hasBenchmark ? `${seriesName}`
                    : hasRecentDataOnly ? momentTimestamp.format(DatetimeFormat.ShortMonthDateTime2)
                    : momentTimestamp.format(DatetimeFormat.ShortMonthDateYear);
                return {
                    name: displayName,
                    value: displayFormattedMonitoringMetricValue(type, value + (threshold ?? 0), forcedUnit),
                };
            },
            shared: true,
            showCrosshairs: true,
            title: () => {
                const momentTimestamp = dayjs.utc(lastFormatterTimestamp).local();
                return hasRecentDataOnly ?
                        momentTimestamp.format(DatetimeFormat.ShortMonthDateTime2)
                    :   momentTimestamp.format(DatetimeFormat.ShortMonthDateYear);
            },
        },
        annotations,
        xAxis: {
            range: [0, 0.95],
            showLast: false,
            line: null,
            nice: true,
            min: minXAxis,
            max: maxXAxis,
            tickInterval:
                adoptionMode ? undefined
                : hasRecentDataOnly ? ONE_HOUR
                : ONE_HOUR * 24,
            tickLine: {
                style: {
                    stroke: chartColors.gridLine,
                    lineWidth: 2,
                },
            },
            grid: {
                line: {
                    style: {
                        stroke: chartColors.gridLine,
                        lineWidth: 2,
                    },
                },
            },
        },

        line: {
            style: (datum: any) => {
                if (hasBenchmark) {
                    return datum.seriesName === finalData[0]?.seriesName ? styleCurrentLine : styleAdditionalLine;
                }
                return styleCurrentLine;
            },
        },
        areaStyle: (datum: any) => {
            if (hasBenchmark) {
                return datum.seriesName === finalData[0]?.seriesName ? areaCurrentLineStyle : { fillOpacity: 0 };
            }
            return areaCurrentLineStyle;
        },
    };

    return BASE_GRAPH_CONFIG;
};

export interface MonitoringStatCardBaseProps extends GroupedMonitoringStatistic {
    isLoading?: boolean;
    releasedAt: string;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    disableLoadShimmer?: boolean;
    shouldApplyRestrictedTooltipForRollouts?: boolean;
    shouldShowDisabledUI?: boolean;
    description?: string;
    featureFlags: FeatureFlagTimeSeriesDatum[];
    onShowFeatureFlag?: (flag: FeatureFlag) => void;
    onInspectFeatureFlag?: (chartName: string) => void;
    // When set, the chart x-axis is adoption % (mapped from time via this series)
    // instead of time.
    adoptionXAxisSeries?: AdoptionXAxisSeriesPoint[];
}

export const MonitoringStatCardBase: React.FC<MonitoringStatCardBaseProps> = ({
    title,
    customDisplayName,
    description,
    groups,
    integrationType,
    isLoading,
    releasedAt,
    platform,
    appStoreType,
    disableLoadShimmer,
    shouldApplyRestrictedTooltipForRollouts,
    shouldShowDisabledUI,
    featureFlags,
    adoptionXAxisSeries,
    onShowFeatureFlag,
    onInspectFeatureFlag,
}) => {
    const chartTheme = useChartTheme();
    const chartColors = useChartColors();
    const preferredIndex = groups.findIndex((group) => !isNil(group.threshold));

    const [selectedDataPointsGroupIndex, setSelectedDataPointsGroupIndex] = React.useState(
        preferredIndex !== -1 ? preferredIndex : 0,
    );

    const selectedDataPointsGroup = groups[selectedDataPointsGroupIndex];

    const isUsedForAutoAccelerate = groups.some((predicate) => {
        return predicate.automationUsage[AutomationType.AutoPromoteStablePhasedReleases].isUsed;
    });

    const isUsedForAutoHalt = groups.some((predicate) => {
        return predicate.automationUsage[AutomationType.AutoHaltUnstablePhasedReleases].isUsed;
    });

    const isUserReviews = groups.some((group) =>
        Object.values(UserRatingsMetricType).includes(group.type as UserRatingsMetricType),
    );

    const activeMetricDescription =
        selectedDataPointsGroup?.description ||
        (groups.length === 1 ? description : undefined) ||
        (isUserReviews ?
            'This metric represents the average of the written customer reviews for the current version only.'
        :   undefined);

    // the card charts one group at a time, so a rename follows whichever group is selected
    const activeCustomDisplayName =
        selectedDataPointsGroup?.customDisplayName ?? (groups.length === 1 ? customDisplayName : undefined);

    const hasMultipleGroups = groups.length > 1;
    const selectedSeriesHasMultipleConfigurations =
        selectedDataPointsGroup &&
        groups.filter((group) => group.seriesKey === selectedDataPointsGroup.seriesKey).length > 1;

    const selectedGroupLabel =
        selectedSeriesHasMultipleConfigurations ?
            isNil(selectedDataPointsGroup.threshold) ? 'value (no threshold)'
            :   `value (${displayThresholdForMonitoringStatisticGroup(selectedDataPointsGroup)})`
        :   `${selectedDataPointsGroup?.unit ?? ''} ${specificUnitForMetricType(selectedDataPointsGroup?.type)}`.trim();

    const handlePopoverOpen = React.useCallback(() => {
        onInspectFeatureFlag?.(title);
    }, [onInspectFeatureFlag, title]);

    const graphConfig = React.useMemo(() => {
        if (!selectedDataPointsGroup) {
            return null;
        }

        return generateGraphConfig(
            selectedDataPointsGroup,
            featureFlags,
            releasedAt,
            appStoreType,
            chartColors,
            shouldShowDisabledUI,
            onShowFeatureFlag,
            handlePopoverOpen,
            adoptionXAxisSeries,
        );
    }, [
        selectedDataPointsGroup,
        featureFlags,
        releasedAt,
        platform,
        chartColors,
        handlePopoverOpen,
        adoptionXAxisSeries,
    ]);

    return (
        <div
            style={{
                backgroundColor: antGrey.grey2,
                border: `1px solid ${antGrey.grey4}`,
                borderRadius: 8,
                position: 'relative',
                minWidth: 380,
                height: 256,
                overflow: 'hidden',
            }}
        >
            <Space
                direction="vertical"
                style={{
                    padding: 20,
                    width: '100%',
                }}
            >
                {isLoading ?
                    <Skeleton
                        active={!disableLoadShimmer}
                        round={false}
                        style={{
                            padding: 12,
                        }}
                        paragraph={{
                            rows: 3,
                        }}
                    />
                :   <>
                        <div
                            style={{
                                position: 'relative',
                                zIndex: 2,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    position: 'relative',
                                }}
                            >
                                <Space>
                                    {generateAutomationThunderboltIcon({
                                        isUsedForAutoAccelerate,
                                        isUsedForAutoHalt,
                                        prefix: 'This metric is being used for the',
                                    })}

                                    {shouldApplyRestrictedTooltipForRollouts ?
                                        // plan tooltip owns the hover here
                                        <PlanRestrictedFeatureWrapper
                                            feature={SubscriptionFeatureType.Rollouts}
                                            requiresTooltip
                                        >
                                            <Typography.Text
                                                style={{
                                                    color: antGrey.grey8,
                                                }}
                                            >
                                                <strong>{activeCustomDisplayName || title}</strong>
                                            </Typography.Text>
                                        </PlanRestrictedFeatureWrapper>
                                    :   <Tooltip title={renamedMetricTooltip(activeCustomDisplayName, title)}>
                                            <Typography.Text
                                                style={{
                                                    color: antGrey.grey8,
                                                }}
                                            >
                                                <strong>{activeCustomDisplayName || title}</strong>
                                            </Typography.Text>
                                        </Tooltip>
                                    }
                                    {activeMetricDescription && (
                                        <DescriptionPopoverIcon description={activeMetricDescription} />
                                    )}
                                </Space>
                                <IntegrationIcon integrationId={integrationType} size={20} marginTop={2} />
                            </div>
                            {shouldShowDisabledUI ?
                                <div
                                    style={{
                                        backgroundColor: runwayColors.white,
                                        padding: 8,
                                        marginTop: 16,
                                        borderRadius: 4,
                                    }}
                                >
                                    <Typography.Text
                                        style={{
                                            color: antGrey.grey7,
                                            fontSize: 12,
                                        }}
                                    >
                                        User ratings metrics not available on Free plan.
                                    </Typography.Text>
                                </div>
                            :   <Space
                                    align={hasMultipleGroups ? 'center' : 'baseline'}
                                    style={{
                                        marginTop: 10,
                                    }}
                                >
                                    <Typography.Text
                                        style={{
                                            color: antGrey.grey8,
                                            fontSize: 30,
                                        }}
                                    >
                                        {selectedDataPointsGroup.value}
                                    </Typography.Text>
                                    {hasMultipleGroups ?
                                        <Button
                                            icon={<SwapOutlined />}
                                            size="small"
                                            type="dashed"
                                            style={{
                                                fontSize: 12,
                                                borderRadius: 2,
                                                color: runwayColors.primary,
                                            }}
                                            onClick={() => {
                                                setSelectedDataPointsGroupIndex(
                                                    (selectedDataPointsGroupIndex + 1) % groups.length, // rotate through the units
                                                );
                                            }}
                                        >
                                            {selectedGroupLabel}
                                        </Button>
                                    :   <Typography.Text
                                            style={{
                                                color: antGrey.grey6,
                                                fontSize: 12,
                                            }}
                                        >
                                            {selectedDataPointsGroup?.unit}
                                        </Typography.Text>
                                    }
                                </Space>
                            }
                        </div>

                        <div
                            style={{
                                height: shouldShowDisabledUI ? '55%' : '60%',
                                width: '100%',
                                position: 'absolute',
                                zIndex: 100,
                                left: 0,
                                bottom: 10,
                            }}
                        >
                            {graphConfig ?
                                <Area
                                    {...graphConfig}
                                    theme={chartTheme}
                                    style={{
                                        height: '100%',
                                        width: '100%',
                                    }}
                                />
                            :   <div
                                    style={{
                                        display: 'flex',
                                        width: '100%',
                                        height: '100%',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography.Text
                                        style={{
                                            color: antGrey.grey6,
                                        }}
                                    >
                                        Not enough data.
                                    </Typography.Text>
                                </div>
                            }
                        </div>
                    </>
                }
            </Space>
        </div>
    );
};

export const MonitoringStatLoadingCard: React.FC<Partial<MonitoringStatCardBaseProps>> = (props) => {
    return (
        <MonitoringStatCardBase
            isLoading
            key={''}
            title={''}
            groups={[]}
            category={null}
            platform={null}
            appStoreType={props.appStoreType}
            releasedAt=""
            featureFlags={props.featureFlags}
            {...props}
        />
    );
};

export const MonitoringStatCard: React.FC<Omit<MonitoringStatCardBaseProps, 'isLoading'>> = (props) => {
    return <MonitoringStatCardBase {...props} />;
};

interface FeatureFlagPopoverProps {
    batchedDay?: string;
    datums: FeatureFlagTimeSeriesDatum[];
    onShowFeatureFlag: (flag: FeatureFlag) => void;
    onPopoverOpen?: () => void;
}

const FeatureFlagPopover: React.FC<FeatureFlagPopoverProps> = (props: FeatureFlagPopoverProps) => {
    const [popoverOpen, setPopoverOpen] = React.useState(false);
    const sortedDatums = [...props.datums].sort(
        (a, b) => dayjs.utc(b.timestamp).valueOf() - dayjs.utc(a.timestamp).valueOf(),
    );

    return (
        <Popover
            open={popoverOpen}
            onOpenChange={(open) => {
                setPopoverOpen(open);
                if (open) {
                    props.onPopoverOpen?.();
                }
            }}
            arrow={false}
            title={
                props.batchedDay ?
                    <Typography.Title level={4}>
                        {dayjs(props.batchedDay).format(DatetimeFormat.ShortMonthDateAppendix)} changes
                    </Typography.Title>
                :   null
            }
            content={
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                        width: 420,
                        maxHeight: 360,
                        overflowY: 'auto',
                        paddingRight: 4,
                    }}
                >
                    {sortedDatums.map((featureFlagChangeDatum, index) => {
                        const featureFlag = featureFlagChangeDatum.flagAfter ?? featureFlagChangeDatum.flagBefore;
                        const detectedAt = dayjs.utc(featureFlagChangeDatum.timestamp).local();

                        return (
                            <div
                                key={`${featureFlag?.id}-${featureFlagChangeDatum.timestamp}-${index}`}
                                style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'baseline',
                                        gap: 12,
                                    }}
                                >
                                    <Typography.Text
                                        style={{ fontWeight: 600, color: antGrey.grey8 }}
                                        ellipsis={{ tooltip: featureFlag?.name }}
                                    >
                                        {featureFlag?.name}
                                    </Typography.Text>
                                    {featureFlagChangeDatum.flagAfter && (
                                        <Typography.Link
                                            style={{ whiteSpace: 'nowrap', fontSize: 13 }}
                                            onClick={() => {
                                                setPopoverOpen(false);
                                                props.onShowFeatureFlag(featureFlagChangeDatum.flagAfter);
                                            }}
                                        >
                                            View flag <ArrowRightOutlined style={{ fontSize: 11 }} />
                                        </Typography.Link>
                                    )}
                                </div>
                                <Typography.Text style={{ fontSize: 12, color: antGrey.grey7 }}>
                                    Detected {detectedAt.format(DatetimeFormat.ShortMonthDateTime)}
                                </Typography.Text>
                                <FeatureFlagChangeList changes={featureFlagChangeDatum.diff} />
                            </div>
                        );
                    })}
                </div>
            }
        >
            <FlagFilled style={{ fontSize: 12, color: runwayColors.primary }} />
        </Popover>
    );
};
